import * as React from 'react';
import { Modal, ModalHeader, ModalBody, ModalFooter, Button, Progress, Spinner } from 'reactstrap';
import { useSelector } from 'react-redux';
import { useTranslations } from '../../../../store/translations/hooks';
import { TK } from '../../../../store/translations/translationKeys';
import { DateField } from '../../../../components/Forms/fields/DateField';
import { AutocompleteTextField } from '../../../../components/Forms/fields/AutocompleteTextField';
import { getAppointmentsAsync, deleteAppointmentAsync } from '../../../../ajax/requests';
import { getGeneralSettings } from '../../../../store/settings/selectors';
import { getEntitiesDataItems } from '../../../../store/entities/selectors';
import { ApplicationState } from '../../../../store';
import { ProfessionalEntity } from '../../../../store/entities/state';
import { AppointmentData } from '../../../../models/api-models';
import moment from 'moment';

interface DeleteAppointmentsModalProps {
    isOpen: boolean;
    onClose: () => void;
}

type Step = 'select' | 'confirm' | 'delete';

const matchesFilter = (apt: AppointmentData, filter: string): boolean => {
    if (filter.startsWith('prof:')) return apt.professionalName === filter.slice(5);
    if (filter.startsWith('svc:')) return apt.type === filter.slice(4);
    if (filter.startsWith('room:')) return apt.room === filter.slice(5);
    return false;
};

const DeleteAppointmentsModal: React.FC<DeleteAppointmentsModalProps> = ({ isOpen, onClose }) => {
    const t = useTranslations();
    const generalSettings = useSelector(getGeneralSettings);
    const professionals = useSelector<ApplicationState, ProfessionalEntity[]>(getEntitiesDataItems<ProfessionalEntity>('professionals'));

    const [step, setStep] = React.useState<Step>('select');
    const [from, setFrom] = React.useState('');
    const [to, setTo] = React.useState('');
    const [deleteOnly, setDeleteOnly] = React.useState<string[]>([]);
    const [doNotDelete, setDoNotDelete] = React.useState<string[]>([]);
    const [appointments, setAppointments] = React.useState<AppointmentData[]>([]);
    const [loading, setLoading] = React.useState(false);
    const [fetchError, setFetchError] = React.useState<string | null>(null);

    const [deleted, setDeleted] = React.useState(0);
    const [deleteErrors, setDeleteErrors] = React.useState(0);
    const [total, setTotal] = React.useState(0);
    const [done, setDone] = React.useState(false);
    const cancelRef = React.useRef(false);

    React.useEffect(() => {
        if (!isOpen) {
            setStep('select');
            setFrom('');
            setTo('');
            setDeleteOnly([]);
            setDoNotDelete([]);
            setAppointments([]);
            setLoading(false);
            setFetchError(null);
            setDeleted(0);
            setDeleteErrors(0);
            setTotal(0);
            setDone(false);
            cancelRef.current = false;
        }
    }, [isOpen]);

    const filterOptions = React.useMemo(() => {
        const profOptions = professionals.map(p => `prof:${p.name}`);
        const svcOptions = Object.keys(generalSettings?.services ?? {}).map(k => `svc:${k}`);
        const roomOptions = Object.keys(generalSettings?.rooms ?? {}).map(k => `room:${k}`);
        return [...profOptions, ...svcOptions, ...roomOptions];
    }, [professionals, generalSettings]);

    const optionLabelResolver = React.useCallback((opt: string) => {
        if (opt.startsWith('prof:')) return opt.slice(5);
        if (opt.startsWith('svc:')) {
            const key = opt.slice(4);
            return generalSettings?.services?.[key]?.name ?? key;
        }
        if (opt.startsWith('room:')) return opt.slice(5);
        return opt;
    }, [generalSettings]);

    const searchMatcher = React.useCallback((input: string, opt: string) => {
        return optionLabelResolver(opt).toLowerCase().includes(input.toLowerCase());
    }, [optionLabelResolver]);

    const filteredAppointments = React.useMemo(() => {
        let result = appointments;
        if (deleteOnly.length > 0) result = result.filter(apt => deleteOnly.some(f => matchesFilter(apt, f)));
        if (doNotDelete.length > 0) result = result.filter(apt => !doNotDelete.some(f => matchesFilter(apt, f)));
        return result;
    }, [appointments, deleteOnly, doNotDelete]);

    const handleNext = React.useCallback(async () => {
        if (!from || !to) return;
        setLoading(true);
        setFetchError(null);
        try {
            const toEndOfDay = moment(to).endOf('day').format('YYYY-MM-DDTHH:mm:ss');
            const result = await getAppointmentsAsync(from, toEndOfDay);
            setAppointments(result);
            setStep('confirm');
        } catch (e: any) {
            setFetchError(e?.message ?? 'Error');
        } finally {
            setLoading(false);
        }
    }, [from, to]);

    const handleDelete = React.useCallback(async () => {
        cancelRef.current = false;
        const list = filteredAppointments;
        setDeleted(0);
        setDeleteErrors(0);
        setTotal(list.length);
        setDone(false);
        setStep('delete');

        const BATCH = 5;
        let del = 0;
        let err = 0;
        for (let i = 0; i < list.length; i += BATCH) {
            if (cancelRef.current) break;
            const batch = list.slice(i, i + BATCH);
            const results = await Promise.allSettled(batch.map(apt => deleteAppointmentAsync(apt.id, false)));
            for (const r of results) {
                if (r.status === 'fulfilled') { del++; setDeleted(del); }
                else { err++; setDeleteErrors(err); }
            }
        }
        setDone(true);
    }, [filteredAppointments]);

    const handleCancel = React.useCallback(() => {
        cancelRef.current = true;
    }, []);

    const isRangeValid = from && to && from <= to;
    const progress = total > 0 ? Math.round(((deleted + deleteErrors) / total) * 100) : 0;
    const canClose = step !== 'delete' || done;

    return (
        <Modal isOpen={isOpen} toggle={canClose ? onClose : undefined} size="md">
            <ModalHeader toggle={canClose ? onClose : undefined}>
                {t(TK.deleteAppointments)}
            </ModalHeader>
            <ModalBody>
                {step === 'select' && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacer-S)' }}>
                        <p style={{ color: 'var(--text-muted)', margin: 0 }}>
                            {t(TK.deleteAppointmentsSelectRangeHint)}
                        </p>
                        <div style={{ display: 'flex', gap: 'var(--spacer-S)' }}>
                            <div style={{ flex: 1, minWidth: 0 }}>
                                <DateField
                                    label={t(TK.deleteAppointmentsFrom)}
                                    value={from}
                                    onChange={setFrom}
                                />
                            </div>
                            <div style={{ flex: 1, minWidth: 0 }}>
                                <DateField
                                    label={t(TK.deleteAppointmentsTo)}
                                    value={to}
                                    onChange={setTo}
                                    minDate={from || undefined}
                                />
                            </div>
                        </div>
                        <AutocompleteTextField
                            label={t(TK.deleteAppointmentsDeleteOnly)}
                            placeholder={t(TK.deleteAppointmentsDeleteOnlyPlaceholder)}
                            multiSelect
                            options={filterOptions}
                            values={deleteOnly}
                            onValuesChanged={setDeleteOnly}
                            optionLabelResolver={optionLabelResolver}
                            searchMatcher={searchMatcher}
                        />
                        <AutocompleteTextField
                            label={t(TK.deleteAppointmentsDoNotDelete)}
                            multiSelect
                            options={filterOptions}
                            values={doNotDelete}
                            onValuesChanged={setDoNotDelete}
                            optionLabelResolver={optionLabelResolver}
                            searchMatcher={searchMatcher}
                        />
                        {fetchError && (
                            <div style={{ color: 'var(--danger-color)', fontSize: '0.9rem' }}>
                                {fetchError}
                            </div>
                        )}
                    </div>
                )}

                {step === 'confirm' && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacer-S)' }}>
                        {filteredAppointments.length === 0 ? (
                            <p style={{ color: 'var(--text-muted)', margin: 0 }}>
                                {t(TK.deleteAppointmentsNone)}
                            </p>
                        ) : (
                            <div style={{
                                background: 'var(--warning-bg)',
                                border: `var(--border-thin) solid var(--warning-border)`,
                                borderRadius: '6px',
                                padding: 'var(--spacer-S)',
                                color: 'var(--text-body)',
                            }}>
                                {t(TK.deleteAppointmentsConfirmCount).replace('{count}', String(filteredAppointments.length))}
                            </div>
                        )}
                    </div>
                )}

                {step === 'delete' && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacer-S)' }}>
                        <Progress
                            value={progress}
                            color={done ? (deleteErrors > 0 ? 'warning' : 'success') : 'primary'}
                            animated={!done}
                        />
                        <div style={{ display: 'flex', gap: 'var(--spacer-M)', fontSize: '0.9rem' }}>
                            <span>
                                <b style={{ color: 'var(--success-color)' }}>{deleted}</b>
                                {' '}{t(TK.deleteAppointmentsDeleted)}
                            </span>
                            {deleteErrors > 0 && (
                                <span>
                                    <b style={{ color: 'var(--danger-color)' }}>{deleteErrors}</b>
                                    {' '}{t(TK.deleteAppointmentsErrors)}
                                </span>
                            )}
                        </div>
                        <p style={{ color: 'var(--text-muted)', margin: 0, fontSize: '0.9rem' }}>
                            {done
                                ? t(TK.deleteAppointmentsDoneMsg)
                                : `${t(TK.deleteAppointmentsDeleting)} (${deleted + deleteErrors}/${total})`
                            }
                        </p>
                    </div>
                )}
            </ModalBody>
            <ModalFooter>
                {step === 'select' && (
                    <>
                        <Button color="secondary" onClick={onClose}>
                            {t(TK.cancel)}
                        </Button>
                        <Button color="danger" disabled={!isRangeValid || loading} onClick={handleNext}>
                            {loading && <Spinner size="sm" />}
                            {loading ? null : t(TK.next)}
                        </Button>
                    </>
                )}
                {step === 'confirm' && (
                    <>
                        <Button color="secondary" onClick={() => setStep('select')}>
                            {t(TK.back)}
                        </Button>
                        {filteredAppointments.length > 0 ? (
                            <Button color="danger" onClick={handleDelete}>
                                {t(TK.delete)}
                            </Button>
                        ) : (
                            <Button color="secondary" onClick={onClose}>
                                {t(TK.close)}
                            </Button>
                        )}
                    </>
                )}
                {step === 'delete' && (
                    <>
                        {!done ? (
                            <Button color="secondary" onClick={handleCancel}>
                                {t(TK.cancel)}
                            </Button>
                        ) : (
                            <Button color="primary" onClick={onClose}>
                                {t(TK.close)}
                            </Button>
                        )}
                    </>
                )}
            </ModalFooter>
        </Modal>
    );
};

export default DeleteAppointmentsModal;
